import { Injectable } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import type { TemplateNode } from './certification-template-editor.component';

@Injectable({ providedIn: 'root' })
export class TemplateDragStateService {
  private draggedNode: TemplateNode | null = null;
  private insideTarget: TemplateNode | null = null;
  private rootTarget: TemplateNode[] | null = null;

  get hasInsideTarget(): boolean {
    return this.insideTarget !== null || this.rootTarget !== null;
  }

  start(node: TemplateNode): void {
    this.draggedNode = node;
    this.insideTarget = null;
    this.rootTarget = null;
  }

  reset(): void {
    this.draggedNode = null;
    this.insideTarget = null;
    this.rootTarget = null;
  }

  enterNode(node: TemplateNode): boolean {
    if (!this.canMoveInto(node)) return false;
    this.rootTarget = null;
    this.insideTarget = node;
    return true;
  }

  enterRoot(nodes: TemplateNode[]): boolean {
    if (!this.draggedNode) return false;
    this.insideTarget = null;
    this.rootTarget = nodes;
    return true;
  }

  leaveNode(node: TemplateNode): void {
    if (this.insideTarget === node) this.insideTarget = null;
  }

  leaveRoot(nodes: TemplateNode[]): void {
    if (this.rootTarget === nodes) this.rootTarget = null;
  }

  isInsideTarget(node: TemplateNode): boolean {
    return this.insideTarget === node;
  }

  isRootTarget(nodes: TemplateNode[]): boolean {
    return this.rootTarget === nodes;
  }

  canMoveInto(target: TemplateNode, dragged: TemplateNode | null = this.draggedNode): boolean {
    if (!dragged) return false;
    return dragged !== target && !this.containsNode(dragged, target);
  }

  applyInsideDrop(event: CdkDragDrop<TemplateNode[]>): boolean {
    const dragged = (event.item.data as TemplateNode | undefined) ?? this.draggedNode;
    if (!dragged) return false;

    const source = event.previousContainer.data;
    const sourceIndex = source.indexOf(dragged);
    if (sourceIndex < 0) return false;

    if (this.rootTarget) {
      const rootNodes = this.rootTarget;
      if (source === rootNodes) {
        moveItemInArray(source, sourceIndex, Math.max(0, source.length - 1));
      } else {
        transferArrayItem(source, rootNodes, sourceIndex, rootNodes.length);
      }
      this.reset();
      return true;
    }

    const target = this.insideTarget;
    if (!target || !this.canMoveInto(target, dragged)) return false;

    const targetChildren = target.children ?? (target.children = []);
    if (source === targetChildren) {
      moveItemInArray(source, sourceIndex, Math.max(0, source.length - 1));
    } else {
      transferArrayItem(source, targetChildren, sourceIndex, targetChildren.length);
    }

    target.type = 'object';
    target.expanded = true;
    this.reset();
    return true;
  }

  private containsNode(parent: TemplateNode, candidate: TemplateNode): boolean {
    if (!parent.children?.length) return false;
    return parent.children.some(child => child === candidate || this.containsNode(child, candidate));
  }
}
